import { Navigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { DateRangeFields } from '@/features/campaigns/components/DateRangeFields';
import { ScheduleStep } from '@/features/campaigns/components/ScheduleStep';
import { PreviewStep } from '@/features/campaigns/components/PreviewStep';
import { SessionTimer } from '@/features/campaigns/components/SessionTimer';
import { CampaignSuccessModal } from '@/features/campaigns/components/CampaignSuccessModal';
import { useCampaignRelaunchPage } from '@/features/campaigns/hooks/useCampaignRelaunchPage';
import { AI_SESSION_SECONDS } from '@/features/campaigns/constants/campaignOptions';
import { DepositModal } from '@/features/wallet/components/DepositModal';
import { formatMoney } from '@/shared/lib/format/money';
import { CampaignBusyOverlay } from '@/features/campaigns/components/CampaignBusyOverlay';

/** 2-step relaunch flow for an already-published campaign: new dates/slots, then review & pay. Not part of the create wizard. */
export function CampaignRelaunchPage() {
  const page = useCampaignRelaunchPage();


  if (!page.campaignId) {
    return <Navigate to="/campaigns" replace />;
  }

  if (page.overviewQuery.isPending) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-[var(--dash-brand)]" />
      </div>
    );
  }

  if (!page.overview) {
    return (
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-3 py-24 text-center">
        <p className="m-0 text-sm font-semibold text-[var(--dash-ink)]">
          Campaign not found
        </p>
        <button
          type="button"
          onClick={() => page.navigate('/campaigns')}
          className="rounded-xl bg-[var(--dash-brand)] px-4 py-2 text-sm font-semibold text-white"
        >
          Back to Campaigns
        </button>
      </div>
    );
  }

  const nextLabel = page.currentStep === 2 ? 'Pay & Launch' : 'Relaunch';

  return (
    <div className="mx-auto flex min-h-[70vh] w-full max-w-6xl flex-col">
      {!page.showSuccess ? (
        <SessionTimer
          initialSeconds={AI_SESSION_SECONDS}
          onExpire={page.handleSessionExpiry}
        />
      ) : null}

      <div className="mb-5 flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={page.handleBack}
          className="inline-flex items-center gap-1.5 rounded-xl px-2 py-1.5 text-sm font-semibold text-[var(--dash-muted)] transition hover:bg-white hover:text-[var(--dash-ink)]"
        >
          <ArrowLeft className="h-4 w-4" />
          {page.currentStep === 2 ? 'Previous step' : 'All campaigns'}
        </button>
        <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase ring-1 ring-[var(--dash-line)]">
          Relaunch
        </span>
      </div>

      <div className="camp-fade-up flex-1">
        {page.currentStep === 1 ? (
          <div className="flex w-full flex-col gap-5">
            <header className="text-left">
              <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-brand)] uppercase">
                Relaunch campaign
              </p>
              <h1 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-[var(--dash-ink)]">
                {page.campaignData.campaignName || 'Pick new dates & slots'}
              </h1>
              <p className="mt-1.5 mb-0 max-w-xl text-sm leading-relaxed text-[var(--dash-muted)]">
                Choose a new date range, then book the time slots you want for
                the same screens.
              </p>
            </header>

            <section className="camp-section">
              <DateRangeFields
                campaignData={page.campaignData}
                setCampaignData={page.setCampaignData}
                errors={page.dateErrors}
                clearError={page.clearDateError}
              />
            </section>

            {page.campaignData.startDate && page.campaignData.endDate ? (
              <ScheduleStep
                billboardIds={page.billboardIds}
                campaignData={page.campaignData}
                submitData={page.submitData}
                setSubmitData={page.setSubmitData}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-[var(--dash-line)] bg-white px-6 py-10 text-center">
                <p className="m-0 text-sm font-medium text-[var(--dash-muted)]">
                  Select a start and end date to see available slots.
                </p>
              </div>
            )}
          </div>
        ) : null}

        {page.currentStep === 2 ? (
          <PreviewStep
            data={page.previewData}
            campaignData={page.campaignData}
            creativePreview={page.creativePreview}
            availableBalance={page.availableBalance}
            selectedScreensMeta={page.selectedScreensMeta}
            altMediaIds={[]}
            setAltMediaIds={() => { }}
            onLaunch={page.handleNext}
            isLaunching={page.isBusy}
            isRelaunch
            onBack={page.handleBack}
            coupon={page.coupon}
            bookingSlotCount={page.submitData.bookingSlots.length}
          />
        ) : null}

        <div className="camp-sticky-bar">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="m-0 text-left text-xs text-[var(--dash-muted)] sm:max-w-[50%]">
              {page.currentStep === 1
                ? `${page.submitData.bookingSlots.length} time slot${page.submitData.bookingSlots.length === 1 ? '' : 's'} booked.`
                : page.belowMinOrder
                  ? 'Order below INR 500 — go back and increase slots to continue.'
                  : page.needsTopUp
                    ? 'Insufficient balance — pay to top up and launch in one step.'
                    : 'Confirm pricing and wallet balance before launch.'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={page.handleBack}
                className="rounded-xl border border-[var(--dash-line)] bg-white px-4 py-2.5 text-sm font-semibold text-[var(--dash-ink)] transition hover:bg-[var(--dash-soft)]"
              >
                {page.currentStep === 1 ? 'Cancel' : 'Back'}
              </button>
              <button
                type="button"
                disabled={
                  page.isBusy ||
                  page.coupon.isBusy ||
                  (page.currentStep === 1 && !page.submitData.bookingSlots.length) ||
                  (page.currentStep === 2 &&
                    (page.belowMinOrder || page.exceedsMaxOrder))
                }
                onClick={page.handleNext}
                className="dash-btn-gradient inline-flex min-w-[160px] items-center justify-center gap-2 rounded-2xl px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {page.isBusy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing…
                  </>
                ) : (
                  nextLabel
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <CampaignBusyOverlay
        open={page.isBusy}
        title={
          page.currentStep === 2 ? 'Launching campaign…' : 'Saving…'
        }
        message={
          page.currentStep === 2
            ? 'Confirming payment and relaunching your campaign. Please don’t close this page.'
            : 'Saving your progress. Please don’t close this page.'
        }
      />

      <DepositModal
        open={page.depositOpen}
        onClose={page.closeDeposit}
        availableBalance={page.availableBalance}
        initialAmount={page.depositAmount}
        minAmount={page.depositAmount}
        title="Add funds to launch"
        subtitle={`Need ${formatMoney(page.launchShortfall)} more. Pay with Razorpay, then we launch automatically.`}
        confirmLabel="Pay with Razorpay"
        onSuccess={page.handleDepositSuccess}
      />

      <CampaignSuccessModal
        open={page.showSuccess}
        campaignId={page.previewData.campaignId || page.campaignId}
        onDone={() => page.navigate('/campaigns')}
        variant="relaunch"
      />
    </div>
  );
}
